import * as get from 'lodash/get';
import * as isArray from 'lodash/isArray';
import * as isNil from 'lodash/isNil';

import { FactOperator } from '../fact-operators/fact-operator';
import { FactResult } from '../models/fact-result.model';
import { Operator } from '../operators';
import { Rule } from './rule';

/**
 * Conditions consist of a fact to evaluate against a target.
 * The fact and target are evaluated against the provided data, and processed via the defined operator.
 */
export class Condition implements Rule {
	constructor(public fact: string | string[], public operator: Operator, public target: string | number, public factOperator?: FactOperator) { }

	public evaluate(portData: any, defaultValue?: any): boolean {
		try {
			const evaluationResult: boolean = this.operator.evaluate(this.getValue(portData, this.fact, defaultValue, this.factOperator), this.getValue(portData, this.target));
			return evaluationResult;
		} catch (error) {
			throw new Error('RULES_ENGINE_ERROR: evaluation error occurred with message:' + (<Error>error).message);
		}
	}

	// wrapper around get() in case value is a number
	/**
	 * Return value for the provided fact. 
	 * Fact can either be 
	 * - array of string - in this case a valid factOperator needs to be provided in the config
	 * - string 
	 * 
	 * If the fact is an array of string, the values are mapped as per the same rules of the fact as a string:
	 * - If factData is valid, it is returned.
	 * - If factData exists, but it has error, an error output is returned.
	 * - If factData is null and defaultValue is provided, defaultValue is returned.
	 * - If factData is null and defaultValue is not provided, an error output is returned.
	 * 
	 * @param portData 
	 * @param fact 
	 * @param defaultValue value to use when fact is missing from portData
	 * @param factOperator operator to be used when fact is array, null if not provided 
	 */
	private getValue(portData: any, fact: any, defaultValue?: any, factOperator?: FactOperator): any {
		if (this.isFactArray(fact, factOperator) || this.isFactString(fact)) {

			let factData;
			if (this.isFactArray(fact, factOperator)) {
				const values = fact.map(factKey => this.getFactDataForStringFact(portData, factKey, defaultValue));
				factData = factOperator.calculate(values);
			} else {
				factData = this.getFactDataForStringFact(portData, fact, defaultValue);
			}

			return factData;
		}

		return fact;
	}

	private isFactArray(fact: any, factOperator: FactOperator): boolean {
		return isArray(fact) && !isNil(factOperator);
	}

	private isFactString(fact: any): boolean {
		return typeof fact === 'string';
	}

	private getFactDataForStringFact(portData: any, fact: any, defaultValue?: any): any {
		if (typeof fact === 'string') {
			let factData = get(portData, fact, null);

			if (factData !== null || !isNil(defaultValue)) {

				if (factData?.error) {
					throw new Error((<FactResult>factData).error.debugMessage);
				}

				if (isNil(factData)) {
					factData = defaultValue;
					console.warn(fact + ' data point not found. Using default value: ' + factData);
				}

				return factData instanceof FactResult ? factData.value : factData;
			} else {
				throw new Error(fact + ' data point not found');
			}
		}

		return fact;
	}
}
